/**
 * JINSA – Hero dinámico
 * Slider de 3 vistas construido en código (no imágenes):
 *   1) Stats reales (productos / marcas / % a tiempo / despacho)
 *   2) Grid de marcas con auto-hover secuencial
 *   3) Pedido rápido por lista (SKU x cantidad) con búsqueda real,
 *      carrito y cotización PDF inmediata.
 */
(function () {
  'use strict';

  // ── Custom element: slider principal (3 vistas) ─────────────────────────
  class HeroDynamicSlider extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.track = this.querySelector('[data-hds-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hds-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hds-goto]'));
      this.prevBtn = this.querySelector('[data-hds-prev]');
      this.nextBtn = this.querySelector('[data-hds-next]');
      this.currentEl = this.querySelector('[data-hds-current]');
      this.totalEl = this.querySelector('[data-hds-total]');
      this.nameEl = this.querySelector('[data-hds-slide-name]');
      this.autoplayLabel = this.querySelector('[data-hds-autoplay-label]');

      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true';
      this.delay = parseInt(this.dataset.delay, 10) || 7000;
      this.timer = null;
      this.hovering = false;

      if (this.totalEl) this.totalEl.textContent = String(this.slides.length).padStart(2, '0');

      this.prevBtn && this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
      this.nextBtn && this.nextBtn.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.go(parseInt(dot.dataset.hdsGoto, 10) || 0));
      });

      this.addEventListener('mouseenter', () => { this.hovering = true; this.updateAutoplayState(); });
      this.addEventListener('mouseleave', () => { this.hovering = false; this.updateAutoplayState(); });
      this.addEventListener('focusin', () => { this.hovering = true; this.updateAutoplayState(); });
      this.addEventListener('focusout', () => { this.hovering = false; this.updateAutoplayState(); });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(this.index - 1);
        if (e.key === 'ArrowRight') this.go(this.index + 1);
      });
      document.addEventListener('visibilitychange', () => this.updateAutoplayState());

      this.bindSwipe();
      this.render();
      this.updateAutoplayState();
      this.animateCounters();
    }

    bindSwipe() {
      if (!this.track) return;
      let startX = 0;
      let deltaX = 0;
      const threshold = 40;
      this.track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        deltaX = 0;
      }, { passive: true });
      this.track.addEventListener('touchmove', (e) => {
        deltaX = e.touches[0].clientX - startX;
      }, { passive: true });
      this.track.addEventListener('touchend', () => {
        if (Math.abs(deltaX) > threshold) this.go(this.index + (deltaX < 0 ? 1 : -1));
      });
    }

    go(newIndex) {
      const total = this.slides.length;
      if (!total) return;
      this.index = (newIndex + total) % total;
      this.render();
      this.restartAutoplay();
      this.dispatchEvent(new CustomEvent('hds:slidechange', { detail: { index: this.index } }));
    }

    render() {
      if (this.track) this.track.style.transform = `translateX(-${this.index * 100}%)`;
      this.slides.forEach((slide, i) => slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true'));
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
      if (this.currentEl) this.currentEl.textContent = String(this.index + 1).padStart(2, '0');
      if (this.nameEl) this.nameEl.textContent = (this.slides[this.index] && this.slides[this.index].dataset.slideName) || '';
    }

    updateAutoplayState() {
      const shouldRun = this.autoplay && !this.hovering && !document.hidden;
      if (this.autoplayLabel) this.autoplayLabel.textContent = shouldRun ? 'Autoplay' : 'Pausado';
      if (shouldRun) this.startAutoplay();
      else this.stopAutoplay();
    }

    startAutoplay() {
      if (this.timer) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }

    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.updateAutoplayState();
    }

    animateCounters() {
      const els = this.querySelectorAll('[data-hds-count-to]');
      const duration = 1400;
      els.forEach((el) => {
        const target = parseInt(el.getAttribute('data-hds-count-to'), 10) || 0;
        const start = performance.now();
        function step(now) {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          el.textContent = Math.round(target * eased).toLocaleString('es-MX');
          if (progress < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }
  }

  if (!customElements.get('hero-dynamic-slider')) {
    customElements.define('hero-dynamic-slider', HeroDynamicSlider);
  }

  // ── Botón "Crear cotización" del slide 1: abre la quote-drawer existente ─
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-hds-open-quote]');
    if (!btn) return;
    if (typeof window.toggleJinsaQuoteDrawer === 'function') {
      window.toggleJinsaQuoteDrawer();
    } else {
      window.location.href = '/pages/cotizacion';
    }
  });

  // ── Slide 2: spotlight de marca que rota automáticamente ────────────────
  // El marquee de abajo es puro CSS (scroll infinito + pausa en :hover);
  // aquí solo se maneja la tarjeta grande que va cambiando de marca.
  function initBrandSpotlight(root) {
    const spotlight = root.querySelector('[data-hds-brand-spotlight]');
    const dataEl = root.querySelector('[data-hds-brand-data]');
    if (!spotlight || !dataEl) return;

    let brands = [];
    try {
      brands = JSON.parse(dataEl.textContent) || [];
    } catch (e) {
      brands = [];
    }
    if (!brands.length) return;

    const nameEl = spotlight.querySelector('[data-hds-spotlight-name]');
    const countEl = spotlight.querySelector('[data-hds-spotlight-count]');
    const discountEl = spotlight.querySelector('[data-hds-spotlight-discount]');
    const ctaEl = spotlight.querySelector('[data-hds-spotlight-cta]');
    const progressBar = spotlight.querySelector('[data-hds-spotlight-progress-bar]');
    const prevBtn = spotlight.querySelector('[data-hds-spotlight-prev]');
    const nextBtn = spotlight.querySelector('[data-hds-spotlight-next]');

    const DURATION = 3400;
    let idx = 0;
    let timer = null;
    let paused = false;

    function render() {
      const brand = brands[idx];
      spotlight.style.setProperty('--brand-accent', brand.accent);
      nameEl.textContent = brand.name;
      countEl.textContent = `${brand.count} producto${brand.count === 1 ? '' : 's'}`;
      discountEl.textContent = brand.discount > 0 ? `-${brand.discount}% hoy` : '';
      ctaEl.href = brand.url;

      if (progressBar) {
        progressBar.classList.remove('is-animating');
        void progressBar.offsetWidth; // fuerza reflow para reiniciar la animación
        progressBar.style.animationDuration = `${DURATION}ms`;
        progressBar.classList.add('is-animating');
      }
    }

    function go(newIndex) {
      idx = (newIndex + brands.length) % brands.length;
      render();
      restart();
    }

    function tick() {
      if (paused || document.hidden) return;
      go(idx + 1);
    }

    function start() {
      stop();
      timer = setInterval(tick, DURATION);
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function restart() {
      stop();
      start();
    }

    prevBtn && prevBtn.addEventListener('click', () => go(idx - 1));
    nextBtn && nextBtn.addEventListener('click', () => go(idx + 1));

    spotlight.addEventListener('mouseenter', () => { paused = true; });
    spotlight.addEventListener('mouseleave', () => { paused = false; });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tick();
    });

    render();
    start();
  }

  // ── Slide 3: pedido rápido por lista ────────────────────────────────────
  function initQuickOrder(root) {
    const input = root.querySelector('[data-hds-order-input]');
    if (!input) return;

    const checkBtn = root.querySelector('[data-hds-order-check]');
    const statusEl = root.querySelector('[data-hds-order-status]');
    const resultsEl = root.querySelector('[data-hds-order-results]');
    const totalEl = root.querySelector('[data-hds-order-total]');
    const countOkEl = root.querySelector('[data-hds-count-ok]');
    const countWarnEl = root.querySelector('[data-hds-count-warn]');
    const countBadEl = root.querySelector('[data-hds-count-bad]');
    const addCartBtn = root.querySelector('[data-hds-order-addcart]');
    const pdfBtn = root.querySelector('[data-hds-order-pdf]');

    let debounceTimer = null;
    let requestToken = 0;
    let lastResults = [];

    const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

    function parseLines(text) {
      return text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const match = line.match(/^([A-Za-z0-9._\-\/#]+)\s*[x×]\s*(\d+)/i);
          if (match) return { sku: match[1], qty: Math.max(1, parseInt(match[2], 10)) };
          return { sku: line, qty: 1 };
        });
    }

    async function findCandidateHandles(sku) {
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(sku)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=show&resources[options][fields]=variants.sku,title,vendor`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return [];
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        return products.map((p) => p.handle).filter(Boolean);
      } catch (e) {
        return [];
      }
    }

    async function fetchVariantDetail(handle, sku) {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return null;
        const product = await res.json();
        const target = sku.trim().toLowerCase();
        const variant = (product.variants || []).find((v) => (v.sku || '').trim().toLowerCase() === target);
        if (!variant) return null;
        return { product, variant };
      } catch (e) {
        return null;
      }
    }

    // La respuesta de /search/suggest.json no siempre expone el sku por variante,
    // así que solo se usa para encontrar productos candidatos; el SKU exacto se
    // confirma contra /products/<handle>.js, que sí trae variants[].sku real.
    async function findVariantBySku(sku) {
      const handles = await findCandidateHandles(sku);
      for (const handle of handles) {
        const detail = await fetchVariantDetail(handle, sku);
        if (detail) return detail;
      }
      return null;
    }

    function computeStatus(variant, qty) {
      const managed = variant.inventory_management === 'shopify';
      if (!managed) return variant.available ? 'in_stock' : 'backorder';
      if (typeof variant.inventory_quantity === 'number' && variant.inventory_quantity >= qty) return 'in_stock';
      return 'backorder';
    }

    async function resolveLine(line) {
      const detail = await findVariantBySku(line.sku);
      if (!detail) return Object.assign({}, line, { status: 'not_found' });
      const status = computeStatus(detail.variant, line.qty);
      return Object.assign({}, line, {
        status,
        product: detail.product,
        variant: detail.variant,
        unitPrice: detail.variant.price / 100,
      });
    }

    function renderResults(results) {
      lastResults = results;

      if (!results.length) {
        resultsEl.innerHTML = '<p class="hds__order-empty">Agrega SKUs en la lista para ver disponibilidad y precio aquí.</p>';
        countOkEl.textContent = '0';
        countWarnEl.textContent = '0';
        countBadEl.textContent = '0';
        totalEl.textContent = fmt(0);
        addCartBtn.disabled = true;
        pdfBtn.disabled = true;
        return;
      }

      const counts = { in_stock: 0, backorder: 0, not_found: 0 };
      let total = 0;
      const statusLabels = { in_stock: 'En stock', backorder: 'Bajo pedido', not_found: 'No encontrado' };

      const rowsHtml = results.map((r) => {
        counts[r.status] += 1;
        const price = r.unitPrice || 0;
        const subtotal = price * r.qty;
        if (r.status !== 'not_found') total += subtotal;

        const title = r.product ? r.product.title : r.sku;

        return `
          <div class="hds__order-row" data-status="${r.status}">
            <span class="hds__order-row-dot"></span>
            <div class="hds__order-row-info">
              <div class="hds__order-row-name">${escapeHtml(title)}</div>
              <div class="hds__order-row-meta">${escapeHtml(r.sku)} · x${r.qty} · ${statusLabels[r.status]}</div>
            </div>
            <div class="hds__order-row-price">
              ${r.status !== 'not_found' ? fmt(subtotal) : '—'}
              ${r.status !== 'not_found' ? `<small>${fmt(price)} c/u</small>` : ''}
            </div>
          </div>`;
      }).join('');

      resultsEl.innerHTML = rowsHtml;
      countOkEl.textContent = String(counts.in_stock);
      countWarnEl.textContent = String(counts.backorder);
      countBadEl.textContent = String(counts.not_found);
      totalEl.textContent = fmt(total);

      const hasResolved = results.some((r) => r.status !== 'not_found');
      addCartBtn.disabled = !hasResolved;
      pdfBtn.disabled = !hasResolved;
    }

    function escapeHtml(str) {
      return String(str || '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      })[c]);
    }

    async function runCheck() {
      const lines = parseLines(input.value);
      if (!lines.length) {
        renderResults([]);
        return;
      }
      const token = ++requestToken;
      statusEl.textContent = `Verificando ${lines.length} SKU(s)…`;
      checkBtn.disabled = true;

      const results = await Promise.all(lines.map(resolveLine));

      if (token !== requestToken) return; // respuesta obsoleta, se lanzó otra búsqueda después
      checkBtn.disabled = false;
      statusEl.textContent = '';
      renderResults(results);
    }

    checkBtn.addEventListener('click', runCheck);
    input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(runCheck, 900);
    });

    async function addAllToCart(items) {
      const cart = document.querySelector('cart-sidebar');
      const payload = { items: items.map((r) => ({ id: r.variant.id, quantity: r.qty })) };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        payload.sections = cart.getSectionsToRender().map((s) => s.section);
        payload.sections_url = window.location.pathname;
      }
      const url = (window.JinsaHeroDynamic && window.JinsaHeroDynamic.cartAddUrl)
        || (window.routes && window.routes.cart_add_url)
        || '/cart/add.js';

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error((data && data.description) || 'No se pudo agregar al carrito');

      if (cart && data.sections && typeof cart.renderContents === 'function') {
        cart.renderContents(data);
      }
    }

    function buildQuoteEntry(r) {
      const variant = r.variant;
      const product = r.product;
      const precioFinal = variant.price / 100;
      const precioUnitario = variant.compare_at_price ? variant.compare_at_price / 100 : precioFinal;
      let descuento = 0;
      if (precioUnitario > precioFinal) {
        descuento = Math.round(((precioUnitario - precioFinal) / precioUnitario) * 100);
      }
      let img = '';
      if (variant.featured_image && variant.featured_image.src) img = variant.featured_image.src;
      else if (product.featured_image) img = product.featured_image;

      return {
        no: '0',
        noPto: `gid://shopify/ProductVariant/${variant.id}`,
        modelo: variant.sku,
        cBarras: '',
        sat: '',
        descripcion: product.title,
        marca: product.vendor,
        cantidad: r.qty,
        um: 'PIEZA',
        precioUnitario,
        descuento,
        _img: img,
        _variantId: String(variant.id),
        _precioFinal: precioFinal,
      };
    }

    function mergeIntoCotizacionStorage(entries) {
      const KEY = 'jinsa_cotizacion_lista';
      let list = [];
      try { list = JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { list = []; }

      entries.forEach((entry) => {
        const idx = list.findIndex((p) => String(p._variantId) === String(entry._variantId));
        if (idx !== -1) list[idx].cantidad = (parseInt(list[idx].cantidad, 10) || 0) + entry.cantidad;
        else list.push(entry);
      });
      list.forEach((p, i) => { p.no = String(i + 1); });

      localStorage.setItem(KEY, JSON.stringify(list));
      window.dispatchEvent(new CustomEvent('jinsaCotizacionActualizada'));
    }

    function ensureCotizacionScript() {
      return new Promise((resolve, reject) => {
        if (window.JinsaCotizacion) { resolve(); return; }
        const src = window.JinsaHeroDynamic && window.JinsaHeroDynamic.cotizacionScriptUrl;
        if (!src) { reject(new Error('Falta la URL de jinsa-cotizacion.js')); return; }

        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', reject);
          return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = () => resolve();
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }

    addCartBtn.addEventListener('click', async () => {
      const items = lastResults.filter((r) => r.status !== 'not_found' && r.variant);
      if (!items.length) return;

      const originalLabel = addCartBtn.textContent;
      addCartBtn.disabled = true;
      addCartBtn.textContent = 'Agregando…';
      try {
        await addAllToCart(items);
        addCartBtn.textContent = '¡Agregado!';
        setTimeout(() => {
          addCartBtn.textContent = originalLabel;
          addCartBtn.disabled = false;
        }, 2200);
      } catch (e) {
        alert('No se pudo agregar al carrito. Intenta de nuevo.');
        addCartBtn.textContent = originalLabel;
        addCartBtn.disabled = false;
      }
    });

    pdfBtn.addEventListener('click', async () => {
      const items = lastResults.filter((r) => r.status !== 'not_found' && r.variant);
      if (!items.length) return;

      const originalLabel = pdfBtn.textContent;
      pdfBtn.disabled = true;
      pdfBtn.textContent = 'Generando…';
      try {
        mergeIntoCotizacionStorage(items.map(buildQuoteEntry));
        await ensureCotizacionScript();
        await window.JinsaCotizacion.generatePDF();
      } catch (e) {
        alert('No se pudo generar la cotización. Intenta de nuevo.');
      } finally {
        pdfBtn.textContent = originalLabel;
        pdfBtn.disabled = false;
      }
    });
  }

  function init() {
    document.querySelectorAll('.hero-dynamic').forEach((root) => {
      initBrandSpotlight(root);
      initQuickOrder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();